import React from "react";
import { Star, StarBorder } from "@mui/icons-material";

/**
 * Rating component: Star rating input/display
 * @param {number} value - Current rating value (1-5)
 * @param {function} onChange - Callback when a star is clicked (receives new value)
 * @param {object} iconStyle - Style object for the star icons
 * @param {boolean} readOnly - If true, disables interaction
 * @param {boolean} disabled - If true, disables interaction and dims stars
 */
const RatingStars = ({ value = 0, onChange, iconStyle, readOnly = false, disabled = false }) => {
  const [hovered, setHovered] = React.useState(null);
  const [animate, setAnimate] = React.useState(false);
  const [lastValue, setLastValue] = React.useState(value);

  React.useEffect(() => {
    if (value !== lastValue) {
      setLastValue(value);
      setAnimate(true);
    }
  }, [value]);

  React.useEffect(() => {
    if (animate) {
      const timeout = setTimeout(() => setAnimate(false), 350);
      return () => clearTimeout(timeout);
    }
  }, [animate]);

  const handleClick = (newValue) => {
    if (!readOnly && !disabled && onChange) {
      onChange(newValue);
    }
  };

  const starStyle = {
    ...iconStyle,
    opacity: disabled ? 0.5 : 1,
    transition: 'transform 0.35s cubic-bezier(.36,2,.57,.5)',
  };

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <div style={{ margin: 2 }} key={index}
          onMouseEnter={() => !readOnly && !disabled && setHovered(index + 1)}
          onMouseLeave={() => setHovered(null)}
        >
          <div
            onClick={() => handleClick(index + 1)}
            style={{ cursor: !readOnly && !disabled ? "pointer" : "default" }}
          >
            {hovered !== null
              ? (index < hovered ?
                  <Star
                    style={{
                      ...starStyle,
                      transform: animate && index < value ? 'scale(1.4)' : 'scale(1)'
                    }}
                  />
                : <StarBorder style={starStyle} />)
              : (index < value ?
                  <Star
                    style={{
                      ...starStyle,
                      transform: animate && index < value ? 'scale(1.4)' : 'scale(1)'
                    }}
                  />
                : <StarBorder style={starStyle} />)
            }
          </div>
        </div>
      ))}
    </div>
  );
};

export default RatingStars;
